import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { wipeCryptoStorage } from "@/lib/crypto/clStorage";
import { resetCipherState } from "@/lib/crypto/cipherlock";
import { clearAllPreviews } from "@/lib/crypto/previewStore";
import { setBubbleKey } from "@/lib/bubbleColor";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// On login/app-open the SERVER bubble_color wins and overwrites local (mobile mirrors this).
const syncBubble = (u) => {
  if (u?.bubble_color) setBubbleKey(u.bubble_color);
};

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() =>
    localStorage.getItem("bloq_token"),
  );
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem("bloq_token"));

  const applyAuth = (tok, usr) => {
    localStorage.setItem("bloq_token", tok);
    setTokenState(tok);
    setUser(usr);
    syncBubble(usr);
  };

  const logout = () => {
    localStorage.removeItem("bloq_token");
    // Purge all E2EE key material + preview flags so nothing lingers on a shared device.
    resetCipherState();
    clearAllPreviews();
    wipeCryptoStorage();
    setTokenState(null);
    setUser(null);
  };

  const login = async (email, password, turnstileToken) => {
    const data = await api.login(email, password, turnstileToken);
    // 2FA-enabled accounts get no token here — the caller must complete /auth/2fa/verify.
    if (data?.twofa_required) return data;
    applyAuth(data.token, data.user);
    return data;
  };

  // Second leg of a 2FA login: exchange the short-lived ticket + SMS code for a token.
  const verifyTwoFactor = async (ticket, code) => {
    const data = await api.verify2fa(ticket, code);
    applyAuth(data.token, data.user);
    return data;
  };

  const signup = async (payload) => {
    const data = await api.signup(payload);
    if (data.token) applyAuth(data.token, data.user);
    return data;
  };

  const refreshUser = async () => {
    const u = await api.getMe();
    setUser(u);
    syncBubble(u);
    return u;
  };

  // Self-service account deletion — backend deletes, then we wipe the local session/keys.
  const deleteAccount = async (password) => {
    await api.deleteAccount(password);
    logout();
  };

  useEffect(() => {
    if (token) {
      api
        .getMe()
        .then((u) => { setUser(u); syncBubble(u); })
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{ token, user, loading, login, verifyTwoFactor, signup, logout, refreshUser, setUser, deleteAccount }}
    >
      {children}
    </AuthContext.Provider>
  );
}
